import React, {
  useCallback,
  useState,
  useMemo,
  useContext,
  ReactNode,
} from 'react';
import CrossIcon from 'mdi-react/CloseIcon';
import Button from '@prisjakt/ui-button';
import ListHeader from '@prisjakt/ui-list-header';
import { layout } from '@prisjakt/ui-base';
import { GridRow } from '@prisjakt/ui/list/variants';
import { SlideLeft, SlideRight } from '@prisjakt/ui/hideable/variants';
import { Speed, Delay, Direction } from '@prisjakt/ui/definitions';
import NoScroll from '../../../../components/no-scroll';
import Overlay from '../../../../components/overlay';
import RouteContext from '../../../../routes/context';
import { AggregationSection } from '../../../../types';
import ApplyFilterButton from '../../../category/components/filter/components/apply-filter-button';
import { useAggregationSettings } from '../../hooks';
import * as S from './styles';
import FilterSectionGroup from './group';
import FilterSectionButton from './filter-section-button';
import FilterSection, {
  isValidSection,
  isDisabledSection,
  findUngroupedSections,
  findGroupedSections,
  SupportedSection,
} from '../filter-section';
import { ProductCollectionNonNameSearchFilter } from './types';

const renderFilterSectionButtonList = (
  sectionList: SupportedSection[],
  filters: ProductCollectionNonNameSearchFilter[],
  setCurrentSectionId: (id: string) => void
) => (
  <S.FilterSectionButtonList>
    {sectionList.map(section => (
      <FilterSectionButton
        key={section.id}
        section={section}
        filters={filters}
        onOpen={setCurrentSectionId}
        disabled={isDisabledSection(section)}
      />
    ))}
  </S.FilterSectionButtonList>
);

type Props = {
  sections: AggregationSection[];
  filters: ProductCollectionNonNameSearchFilter[];
  setMobileOpen: (open: boolean) => void;
  productsCount: number;
  clearFilter: (property?: string) => void;
};

function MobileFilterView(props: {
  slideDirection: Direction.Left | Direction.Right;
  visible: boolean;
  children: ReactNode;
}) {
  const Hideable = {
    [Direction.Left]: SlideLeft,
    [Direction.Right]: SlideRight,
  }[props.slideDirection];
  return (
    <Hideable visible={props.visible} speed={Speed.Fast} delayIn={Delay.Short}>
      <GridRow gap={layout.spacing.rem.medium}>{props.children}</GridRow>
    </Hideable>
  );
}

const OverlayFilter = ({
  sections,
  filters,
  setMobileOpen,
  productsCount,
  clearFilter,
}: Props) => {
  const { dispatch } = useContext(RouteContext);
  const [currentSectionId, setCurrentSectionId] = useState<
    AggregationSection['id']
  >();
  const currentSection = useMemo(
    () => sections.find(({ id }) => id === currentSectionId),
    [sections, currentSectionId]
  );
  const aggregationSettings = useAggregationSettings(currentSectionId || '');
  const goBack = useCallback(() => {
    if (aggregationSettings?.query) {
      dispatch({ type: 'AGGREGATION_SEARCH', id: currentSectionId, query: '' });
    }
    setCurrentSectionId(undefined);
  }, [dispatch, aggregationSettings, currentSectionId]);
  const close = useCallback(() => {
    setMobileOpen(false);
    goBack();
  }, [setMobileOpen, goBack]);
  const crossIcon = useMemo(() => <CrossIcon />, []);

  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const toggleExpandedGroup = useCallback(
    (id: string) => {
      if (expandedGroups.includes(id)) {
        setExpandedGroups(expandedGroups.filter(groupId => groupId !== id));
      } else {
        setExpandedGroups([...expandedGroups, id]);
      }
    },
    [expandedGroups]
  );

  const ungroupedSections = useMemo(() => findUngroupedSections(sections), [
    sections,
  ]);

  const sectionGroups = useMemo(() => findGroupedSections(sections, filters), [
    sections,
    filters,
  ]);

  const onClear = useCallback(() => {
    // If currentSection is undefined (index view is open), all filters will be cleared.
    // Otherwise, just the filter for that property will be cleared.
    if (currentSection) {
      clearFilter(currentSection.property);
    } else {
      clearFilter();
      close();
    }
  }, [currentSection, clearFilter, close]);

  const canClear = currentSection
    ? filters.some(({ property }) => property === currentSection.property)
    : filters.length > 0;

  return (
    <>
      <NoScroll mobile tablet />
      <Overlay active />
      <S.MobileFilterDialog>
        <S.MobileFilterHeader>
          <ListHeader
            title={currentSection ? currentSection.title : 'Filter'}
            onClick={goBack}
            showBackArrow={!!currentSection}
          >
            <Button
              color="quarternary"
              variant="icon"
              onClick={close}
              icon={crossIcon}
            />
          </ListHeader>
        </S.MobileFilterHeader>

        <MobileFilterView
          visible={!currentSection}
          slideDirection={Direction.Left}
        >
          <S.MobileSectionList>
            {renderFilterSectionButtonList(
              ungroupedSections,
              filters,
              setCurrentSectionId
            )}
            {sectionGroups.map(group => (
              <FilterSectionGroup
                key={group.title}
                group={group}
                expanded={expandedGroups.includes(group.title)}
                toggleExpanded={toggleExpandedGroup}
              >
                {renderFilterSectionButtonList(
                  group.sections,
                  filters,
                  setCurrentSectionId
                )}
              </FilterSectionGroup>
            ))}
          </S.MobileSectionList>
        </MobileFilterView>

        <MobileFilterView
          visible={!!currentSection}
          slideDirection={Direction.Right}
        >
          {currentSection && isValidSection(currentSection) && (
            <FilterSection
              section={currentSection}
              useInfiniteScroll
              appliedFilters={filters}
            />
          )}
        </MobileFilterView>

        <ApplyFilterButton
          productCount={productsCount}
          hasFiltered={filters.length > 0}
          onClick={close}
          onClear={onClear}
          canClear={canClear}
        />
      </S.MobileFilterDialog>
    </>
  );
};

export default OverlayFilter;
